import { v4 as uuid } from "uuid";
import { ChatMessage } from "../types/queries";
import { attachmentContentType } from "./fileType";
import {
  ConversationAttachment,
  MessageAttachment,
  ServerMessageAttachment,
} from "./types";

const CLIENT_ATTACHMENT_STATUSES = new Set<MessageAttachment["status"]>([
  "local",
  "pending",
  "ready",
  "rejected",
]);

type IncomingAttachment = MessageAttachment | ServerMessageAttachment;

const isClientAttachment = (
  attachment: IncomingAttachment,
): attachment is MessageAttachment =>
  "id" in attachment &&
  "status" in attachment &&
  CLIENT_ATTACHMENT_STATUSES.has(attachment.status);

export const toMessageAttachments = (files: File[]): MessageAttachment[] =>
  files.map((file) => ({
    id: uuid(),
    filename: file.name.trim(),
    size: file.size,
    contentType: attachmentContentType(file),
    status: "pending",
  }));

export const toReadyAttachment = (
  attachment: ServerMessageAttachment,
): MessageAttachment => ({
  id: attachment.pathspec,
  pathspec: attachment.pathspec,
  filename: attachment.filename,
  size: attachment.size,
  contentType: attachment.contentType,
  status: "ready",
});

export const normalizeMessageAttachments = (
  message: Omit<ChatMessage, "attachments"> & {
    attachments?: ReadonlyArray<IncomingAttachment>;
  },
): ChatMessage => {
  const attachments = message.attachments?.length
    ? message.attachments.map((attachment) =>
        isClientAttachment(attachment)
          ? attachment
          : toReadyAttachment(attachment),
      )
    : message.attachments
      ? []
      : undefined;

  return {
    ...message,
    id: message.id,
    conversationId: message.conversationId,
    senderId: message.senderId,
    text: message.text,
    messageType: message.messageType,
    sentAt: message.sentAt,
    attachments,
  };
};

/** Linked files become ready. In-flight and failed chips stay until a refetch. */
export const mergeServerAttachments = (
  current: MessageAttachment[] | undefined,
  server: ServerMessageAttachment[],
): MessageAttachment[] => {
  const readyByPath = new Map(
    server.map((attachment) => [
      attachment.pathspec,
      toReadyAttachment(attachment),
    ]),
  );
  const used = new Set<string>();
  const merged: MessageAttachment[] = [];

  for (const local of current ?? []) {
    const serverItem = local.pathspec
      ? readyByPath.get(local.pathspec)
      : undefined;
    if (serverItem?.pathspec) {
      used.add(serverItem.pathspec);
      merged.push({ ...serverItem, id: local.id });
      continue;
    }
    if (local.status === "pending" || local.status === "rejected") {
      merged.push(local);
    }
  }

  for (const item of readyByPath.values()) {
    if (!item.pathspec || used.has(item.pathspec)) continue;
    merged.push(item);
  }

  return merged;
};

export const collectConversationAttachments = (
  messages: ChatMessage[],
): ConversationAttachment[] =>
  messages
    .flatMap((message) =>
      (message.attachments ?? []).map((attachment) => ({
        ...attachment,
        messageId: message.id,
        conversationId: message.conversationId,
        sentAt: message.sentAt,
        senderId: message.senderId,
      })),
    )
    .sort((a, b) => b.sentAt.localeCompare(a.sentAt));
